import React, { useState } from 'react';
import API from '../utils/api';
import { authenticatePasskey, isWebAuthnAvailable } from '../utils/webauthn';
import './SecondFactorPrompt.css';

const SecondFactorPrompt = ({
  tempToken,
  email,
  twoFaEnabled,
  passkeyEnabled,
  onSuccess,
  onCancel
}) => {
  const [tab, setTab] = useState(twoFaEnabled ? 'authenticator' : 'passkey');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [showBackupCode, setShowBackupCode] = useState(false);

  const handleVerifyCode = async () => {
    if (code.length < 6) {
      setError('Code must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await API.post('/auth/verify-2fa', {
        tempToken,
        code
      });

      // Success - pass tokens back to parent
      onSuccess({
        accessToken: res.data.accessToken,
        refreshToken: res.data.refreshToken,
        user: res.data.user
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyAuth = async () => {
    if (!isWebAuthnAvailable()) {
      setError('Passkeys not supported on this device');
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Get authentication options
      const optionsRes = await API.post('/auth/passkey/authentication-options', {
        email
      });

      // Authenticate with passkey
      const credential = await authenticatePasskey(optionsRes.data.options);

      // Send to server
      const res = await API.post('/auth/passkey/authenticate', {
        email,
        credential
      });

      onSuccess({
        accessToken: res.data.accessToken,
        refreshToken: res.data.refreshToken,
        user: res.data.user
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Passkey authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="second-factor-prompt-overlay">
      <div className="second-factor-prompt">
        <h2>Verify Your Identity</h2>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="tabs">
          {twoFaEnabled && (
            <button
              className={`tab-button ${tab === 'authenticator' ? 'active' : ''}`}
              onClick={() => {
                setTab('authenticator');
                setError('');
                setCode('');
                setShowBackupCode(false);
              }}
            >
              Authenticator Code
            </button>
          )}
          {passkeyEnabled && isWebAuthnAvailable() && (
            <button
              className={`tab-button ${tab === 'passkey' ? 'active' : ''}`}
              onClick={() => {
                setTab('passkey');
                setError('');
              }}
            >
              Passkey
            </button>
          )}
        </div>

        <div className="tab-content">
          {tab === 'authenticator' && twoFaEnabled && (
            <div className="authenticator-form">
              {!showBackupCode ? (
                <>
                  <label>6-Digit Code</label>
                  <p className="hint">Enter the code from your authenticator app</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    maxLength="6"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="code-input"
                    autoFocus
                    disabled={loading}
                  />
                  <button
                    className="btn btn-primary btn-full"
                    onClick={handleVerifyCode}
                    disabled={loading || code.length !== 6}
                  >
                    {loading ? 'Verifying...' : 'Verify'}
                  </button>
                  <button
                    className="btn-link"
                    onClick={() => setShowBackupCode(true)}
                    disabled={loading}
                  >
                    Use backup code instead
                  </button>
                </>
              ) : (
                <>
                  <label>Backup Code</label>
                  <p className="hint">Enter one of your backup codes (8 characters)</p>
                  <input
                    type="text"
                    placeholder="00000000"
                    maxLength="8"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    className="code-input"
                    autoFocus
                    disabled={loading}
                  />
                  <button
                    className="btn btn-primary btn-full"
                    onClick={handleVerifyCode}
                    disabled={loading || code.length !== 8}
                  >
                    {loading ? 'Verifying...' : 'Verify'}
                  </button>
                  <button
                    className="btn-link"
                    onClick={() => {
                      setShowBackupCode(false);
                      setCode('');
                    }}
                    disabled={loading}
                  >
                    Back to authenticator code
                  </button>
                </>
              )}
            </div>
          )}

          {tab === 'passkey' && passkeyEnabled && isWebAuthnAvailable() && (
            <div className="passkey-form">
              <p className="hint">
                Click the button below and use your device biometric or PIN to sign in
              </p>
              <button
                className="btn btn-primary btn-full btn-large"
                onClick={handlePasskeyAuth}
                disabled={loading}
              >
                {loading ? 'Authenticating...' : '🔐 Authenticate with Passkey'}
              </button>
            </div>
          )}
        </div>

        <button
          className="btn btn-secondary btn-full"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default SecondFactorPrompt;
